import React, {useEffect, useState} from 'react';
import {Box, Typography, Button, Paper, Grid, TextField, Collapse, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip} from '@mui/material';
import {FormControl, InputLabel, Select, MenuItem, Stack, Alert} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import ToolCard from './ToolCard';
import DeleteDialog from './DeleteDialog';
import SettingsDialog from './SettingsDialog';
import UserProfileDialog from './UserProfileDialog';
import LanguageThemeBar from '../LanguageThemeBar';
import {CircularProgress} from '@mui/material';
import {API_BASE} from '../../apiBase';
import {apiFetch} from '../../api/api';
import VideoTutorials from './VideoTutorials';


export default function HomePage() {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [tools, setTools] = useState([]);
    const [selectedTool, setSelectedTool] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);

    // Provider/Model selector states
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    // New states for settings
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settingsTool, setSettingsTool] = useState(null);

    // User profile dialog
    const [profileOpen, setProfileOpen] = useState(false);

    // API key management states
    const [apiKeyMessage, setApiKeyMessage] = useState('');
    const [configuredKeys, setConfiguredKeys] = useState({});
    const [keyValidationStatus, setKeyValidationStatus] = useState({}); // {provider: 'valid'|'invalid'|'checking'}

    // Add/Edit API key dialog states
    const [showAddKeyDialog, setShowAddKeyDialog] = useState(false);
    const [editingProvider, setEditingProvider] = useState(null); // null = adding new, otherwise editing
    const [newKeyProvider, setNewKeyProvider] = useState('');
    const [newKeyValue, setNewKeyValue] = useState('');
    const [newKeyValidating, setNewKeyValidating] = useState(false);
    const [newKeyValidated, setNewKeyValidated] = useState(false);
    const [newKeyAvailableModels, setNewKeyAvailableModels] = useState([]);
    const [newKeySelectedModel, setNewKeySelectedModel] = useState('');
    const [newKeyError, setNewKeyError] = useState('');

    useEffect(() => {
        const fetchEverything = async () => {
            setLoading(true);
            try {
                // Load profile (for advanced flag + provider/model + allowed pairs)
                const profRes = await apiFetch(`${API_BASE}/profile`);
                if (!profRes.ok) throw new Error(await profRes.text());
                const prof = await profRes.json();
                setUser(prof);
                const configuredApiKeys = prof.configured_api_keys || {};
                setConfiguredKeys(configuredApiKeys);

                // Initialize validation status from stored data
                const initialStatus = {};
                Object.entries(configuredApiKeys).forEach(([provider, info]) => {
                    if (info.source === 'personal' && info.is_valid !== null && info.is_valid !== undefined) {
                        initialStatus[provider] = info.is_valid ? 'valid' : 'invalid';
                    }
                });
                setKeyValidationStatus(initialStatus);

                const res = await apiFetch(`${API_BASE}/get_tools`);
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();
                setTools(data);
            } catch (err) {
                console.error('Error fetching tools', err);
            } finally {
                setLoading(false);
            }
        };
        fetchEverything();
    }, []);

    const handleDeleteTool = async () => {
        if (!selectedTool) return;
        try {
            console.log('[HomePage] Deleting tool:', selectedTool.id);
            const res = await apiFetch(`${API_BASE}/delete_tool/${selectedTool.id}`, {
                method: 'DELETE',
            });
            console.log('[HomePage] Delete response status:', res.status);
            if (!res.ok) {
                const errorText = await res.text();
                console.error('[HomePage] Delete failed:', errorText);
                throw new Error(errorText);
            }
            // Many DELETEs return 204 (no content); we don't need the body.
            setTools((prev) => prev.filter((tool) => tool.id !== selectedTool.id));
            console.log('[HomePage] Tool deleted successfully');
        } catch (err) {
            console.error('[HomePage] Error deleting tool:', err);
            alert(`Failed to delete tool: ${err.message}`);
        } finally {
            setConfirmOpen(false);
            setSelectedTool(null);
        }
    };

    const handleOpenSettings = (tool) => {
        setSettingsTool(tool);
        setSettingsOpen(true);
    };

    const handleSaveSettings = async (updatedData) => {
        if (!settingsTool) return;
        try {
            const res = await apiFetch(`${API_BASE}/update_tool/${settingsTool.id}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(updatedData),
            });
            if (!res.ok) throw new Error(await res.text());

            // Optional: if the API returns the updated tool, use it instead:
            // const saved = await res.json();
            // setTools((prev) => prev.map(t => t.id === settingsTool.id ? saved : t));

            setTools((prev) =>
                prev.map((t) => (t.id === settingsTool.id ? {...t, ...updatedData} : t))
            );
        } catch (err) {
            console.error('Error updating tool', err);
        } finally {
            setSettingsOpen(false);
            setSettingsTool(null);
        }
    };

    // Provider/Model selector handlers
    const handleProviderChange = async (e) => {
        if (!user) return;
        const newProvider = e.target.value;
        // choose a safe default model from allowed map
        const allowed = user.allowed?.[newProvider] || [];
        const fallbackModel = allowed[0] || user.model;
        await saveProviderModel(newProvider, fallbackModel);
    };

    const handleModelChange = async (e) => {
        if (!user) return;
        const newModel = e.target.value;
        await saveProviderModel(user.provider, newModel);
    };

    const saveProviderModel = async (provider, model) => {
        setSaving(true);
        setSaveError('');
        try {
            const res = await apiFetch(`${API_BASE}/profile`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({provider, model})
            });
            if (!res.ok) throw new Error(await res.text());
            const updated = await res.json();
            setUser(updated);
        } catch (err) {
            console.error('Error saving provider/model', err);
            setSaveError(err.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleOpenAddKeyDialog = () => {
        setShowAddKeyDialog(true);
        setEditingProvider(null);
        setNewKeyProvider('');
        setNewKeyValue('');
        setNewKeyValidating(false);
        setNewKeyValidated(false);
        setNewKeyAvailableModels([]);
        setNewKeySelectedModel('');
        setNewKeyError('');
    };

    const handleOpenEditKeyDialog = (provider) => {
        setShowAddKeyDialog(true);
        setEditingProvider(provider);
        setNewKeyProvider(provider);
        setNewKeyValue('');
        setNewKeyValidating(false);
        setNewKeyValidated(false);
        setNewKeyAvailableModels([]);
        setNewKeySelectedModel('');
        setNewKeyError('');
    };

    // Check if a provider is already configured
    const isProviderConfigured = (provider) => {
        return configuredKeys && configuredKeys[provider];
    };

    const handleCloseAddKeyDialog = () => {
        setShowAddKeyDialog(false);
        setEditingProvider(null);
        setNewKeyProvider('');
        setNewKeyValue('');
        setNewKeyValidating(false);
        setNewKeyValidated(false);
        setNewKeyAvailableModels([]);
        setNewKeySelectedModel('');
        setNewKeyError('');
    };

    const handleValidateApiKey = async () => {
        if (!newKeyProvider || !newKeyValue) {
            setNewKeyError('Please select a provider and enter an API key');
            return;
        }

        // Check if provider is already configured (only when adding new, not editing)
        if (!editingProvider && isProviderConfigured(newKeyProvider)) {
            setNewKeyError(`API key for ${newKeyProvider} is already configured. Please delete it first to add a new one.`);
            return;
        }

        setNewKeyValidating(true);
        setNewKeyError('');
        try {
            const res = await apiFetch(`${API_BASE}/user/api-keys/validate`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    provider: newKeyProvider,
                    api_key: newKeyValue
                })
            });

            if (!res.ok) {
                const error = await res.text();
                throw new Error(error);
            }

            const result = await res.json();
            setNewKeyValidated(true);
            setNewKeyAvailableModels(result.available_models || []);
            setNewKeySelectedModel(result.available_models?.[0] || '');
            setNewKeyError('');

        } catch (err) {
            console.error('Error validating API key', err);
            setNewKeyError(err.message || 'Invalid API key');
            setNewKeyValidated(false);
        } finally {
            setNewKeyValidating(false);
        }
    };

    const handleSaveApiKey = async () => {
        if (!newKeyProvider || !newKeyValue || !newKeyValidated) {
            setNewKeyError('Please validate the API key first');
            return;
        }

        try {
            const res = await apiFetch(`${API_BASE}/user/api-keys`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    provider: newKeyProvider,
                    api_key: newKeyValue
                })
            });

            if (!res.ok) {
                const error = await res.text();
                throw new Error(error);
            }

            const result = await res.json();
            setApiKeyMessage(`✅ API key for ${newKeyProvider} saved successfully`);

            // Close dialog
            handleCloseAddKeyDialog();

            // Refresh profile to get updated configured keys and allowed models
            const profRes = await apiFetch(`${API_BASE}/profile`);
            if (profRes.ok) {
                const prof = await profRes.json();
                setUser(prof);
                setConfiguredKeys(prof.configured_api_keys || {});
            }

            // Automatically trigger validation check for the newly added key
            await checkKeyValidation(newKeyProvider);

        } catch (err) {
            console.error('Error saving API key', err);
            setNewKeyError(err.message || 'Failed to save API key');
        }
    };

    const handleDeleteApiKey = async (provider) => {
        if (!window.confirm(`Delete API key for ${provider}?`)) return;

        try {
            const res = await apiFetch(`${API_BASE}/user/api-keys/${provider}`, {
                method: 'DELETE'
            });

            if (!res.ok) {
                const error = await res.text();
                throw new Error(error);
            }

            setApiKeyMessage(`✅ API key for ${provider} deleted`);

            // Remove from configured keys and validation status
            setConfiguredKeys(prev => {
                const updated = {...prev};
                delete updated[provider];
                return updated;
            });
            setKeyValidationStatus(prev => {
                const updated = {...prev};
                delete updated[provider];
                return updated;
            });

        } catch (err) {
            console.error('Error deleting API key', err);
            setApiKeyMessage(`❌ ${err.message || 'Failed to delete API key'}`);
        }
    };

    // Check validation status of a specific API key
    const checkKeyValidation = async (provider) => {
        setKeyValidationStatus(prev => ({...prev, [provider]: 'checking'}));

        try {
            const res = await apiFetch(`${API_BASE}/user/api-keys/${provider}/check`, {
                method: 'GET'
            });

            if (!res.ok) {
                throw new Error('Validation check failed');
            }

            const result = await res.json();
            setKeyValidationStatus(prev => ({
                ...prev,
                [provider]: result.valid ? 'valid' : 'invalid'
            }));

            // Update configuredKeys with new validation info
            if (result.last_validated_at) {
                setConfiguredKeys(prev => ({
                    ...prev,
                    [provider]: {
                        ...prev[provider],
                        last_validated_at: result.last_validated_at,
                        is_valid: result.valid
                    }
                }));
            }
        } catch (err) {
            console.error(`Error checking ${provider} key validation`, err);
            setKeyValidationStatus(prev => ({...prev, [provider]: 'invalid'}));
        }
    };

    // No automatic validation - user can manually recheck using the refresh button


    return (
        <Box display="flex" flexDirection="column" height="100vh" position="relative" pb={6}>
            {/* Top bar */}
            <Box position="absolute" top={10} left={10} display="flex" gap={1} alignItems="center">
                <Tooltip title={t("account_settings") || "Account Settings"}>
                    <IconButton onClick={() => setProfileOpen(true)} color="primary">
                        <SettingsIcon />
                    </IconButton>
                </Tooltip>
                <Button variant="outlined" onClick={() => {
                    localStorage.removeItem('token');
                    navigate('/');
                }}>
                    {t("logout")}
                </Button>
            </Box>
            <Box position="absolute" top={10} right={10}>
                <LanguageThemeBar/>
            </Box>

            <Box mt={5} textAlign="center">
                <Typography variant="h3" mb={2}>{t("homepage")}</Typography>
            </Box>

            {/* API Key Management - only for advanced mode users */}
            {user?.can_change_provider_model && (
                <Paper elevation={3} sx={{p: 2, m: 3}}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="h6">{t("api_providers") || "API Providers"}</Typography>
                        {user.organization && (
                            <Typography variant="caption" color="text.secondary">
                                {t("organization") || "Organization"}: {user.organization.name}
                            </Typography>
                        )}
                    </Box>

                    {/* Show warning if no providers configured */}
                    {Object.keys(configuredKeys).length === 0 && (
                        <Alert severity="info" sx={{mb: 2}}>
                            {t("no_providers_configured") || "No API keys configured. Please add an API key below to get started."}
                        </Alert>
                    )}

                    {/* API Key Management Section */}
                    <Box>

                                {/* Show configured API keys */}
                                {Object.keys(configuredKeys).length > 0 && (
                                    <Box mb={1.5}>
                                        <Stack spacing={0.5}>
                                            {Object.entries(configuredKeys).map(([provider, info]) => {
                                                const validationStatus = keyValidationStatus[provider];
                                                const isPersonal = info.source === 'personal';

                                                // Determine status
                                                let statusIcon = null;
                                                let statusColor = 'default';

                                                if (isPersonal) {
                                                    if (validationStatus === 'valid') {
                                                        statusIcon = <CheckCircleIcon fontSize="small" color="success"/>;
                                                    } else if (validationStatus === 'invalid') {
                                                        statusIcon = <ErrorIcon fontSize="small" color="error"/>;
                                                    } else if (validationStatus === 'checking') {
                                                        statusIcon = <CircularProgress size={16}/>;
                                                    }
                                                } else {
                                                    statusIcon = <CheckCircleIcon fontSize="small" color="info"/>;
                                                }

                                                return (
                                                    <Box key={provider} display="flex" alignItems="center" justifyContent="space-between"
                                                         sx={{py: 0.25, px: 1, borderRadius: 1, '&:hover': {bgcolor: 'action.hover'}}}>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            {statusIcon}
                                                            <Typography variant="body2" sx={{fontWeight: 'medium'}}>
                                                                {provider.toUpperCase()}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                ({t(info.source) || info.source})
                                                            </Typography>
                                                            {info.last_validated_at && (
                                                                <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.7rem'}}>
                                                                    • {new Date(info.last_validated_at).toLocaleString('en-GB', { timeZone: 'Europe/Zurich', dateStyle: 'short', timeStyle: 'short' })}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        {isPersonal && (
                                                            <Box display="flex" gap={0.5}>
                                                                <Tooltip title={t("edit_api_key") || "Edit"}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleOpenEditKeyDialog(provider)}
                                                                    >
                                                                        <EditIcon fontSize="small"/>
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title={t("recheck_api_key") || "Recheck"}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => checkKeyValidation(provider)}
                                                                        disabled={validationStatus === 'checking'}
                                                                    >
                                                                        <RefreshIcon fontSize="small"/>
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title={t("delete_api_key") || "Delete"}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteApiKey(provider)}
                                                                        color="error"
                                                                    >
                                                                        <DeleteIcon fontSize="small"/>
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                )}

                                {/* Add new API key button */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon/>}
                                    onClick={handleOpenAddKeyDialog}
                                >
                                    {t("add_api_key") || "Add API Key"}
                                </Button>

                                {apiKeyMessage && (
                                    <Alert severity="success" sx={{mt: 1.5}} onClose={() => setApiKeyMessage('')}>
                                        {apiKeyMessage}
                                    </Alert>
                                )}
                    </Box>
                </Paper>
            )}

            {/* Playground */}
            <Paper elevation={3} sx={{p: 3, m: 3}}>
                <Typography variant="h5" mb={3}>{t("playground")}</Typography>
                <Typography variant="body1" mb={2}>{t("playground_description")}</Typography>
                <Button
                    variant="contained"
                    startIcon={<PlayArrowIcon/>}
                    onClick={() => navigate('/viewer')}
                >
                    {t("open_playground")}
                </Button>
            </Paper>

            {/* Tools Section */}
            <Paper elevation={3} sx={{p: 3, m: 3}}>
                <Typography variant="h5" mb={3}>{t("my_tools")}</Typography>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                        <CircularProgress/>
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={2}>
                            {tools.map((tool) => (
                                <Grid item xs={12} sm={6} md={3} key={tool.id}>
                                    <ToolCard
                                        tool={tool}
                                        user={user}
                                        onLaunch={() => navigate(`/tool/${tool.id}/upload`, {state: {toolName: tool.name}})}
                                        onDeleteRequest={() => {
                                            setSelectedTool(tool);
                                            setConfirmOpen(true);
                                        }}
                                        onSettings={() => handleOpenSettings(tool)}
                                        onHistory={() => navigate(`/tool/${tool.id}/history`, {state: {toolName: tool.name}})}
                                        onUpdateProviderModel={async (toolId, provider, model) => {
                                            try {
                                                const res = await apiFetch(`${API_BASE}/update_tool/${toolId}`, {
                                                    method: 'PUT',
                                                    headers: {'Content-Type': 'application/json'},
                                                    body: JSON.stringify({
                                                        name: tool.name,
                                                        description: tool.description,
                                                        provider,
                                                        model
                                                    }),
                                                });
                                                if (!res.ok) throw new Error(await res.text());
                                                setTools((prev) =>
                                                    prev.map((t) => (t.id === toolId ? {...t, provider, model} : t))
                                                );
                                            } catch (err) {
                                                console.error('Error updating tool provider/model', err);
                                            }
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        <Box mt={7}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon/>}
                                onClick={() => navigate('/create-tool')}
                            >
                                {t("create_new_tool")}
                            </Button>
                        </Box>
                    </>
                )}
            </Paper>

            {/* Confirm delete */}
            <DeleteDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleDeleteTool}
                t={t}
            />

            {/* Settings dialog */}
            <SettingsDialog
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                onSave={handleSaveSettings}
                initialName={settingsTool ? settingsTool.name : ""}
                initialDescription={settingsTool ? settingsTool.description : ""}
                t={t}
            />

            {/* Video Tutorials Section */}
            <Paper elevation={3} sx={{p: 3, m: 3, mb: 6}} id="video-tutorials">
                <VideoTutorials/>
            </Paper>

            {/* Add/Edit API Key Dialog */}
            <Dialog open={showAddKeyDialog} onClose={handleCloseAddKeyDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingProvider
                        ? (t("edit_api_key_title") || `Edit API Key for ${editingProvider}`)
                        : (t("add_new_api_key") || "Add New API Key")}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{pt: 2}}>
                        {/* Step 1: Select Provider (disabled when editing) */}
                        <FormControl fullWidth sx={{mb: 2}}>
                            <InputLabel id="new-key-provider-label">{t("provider") || "Provider"}</InputLabel>
                            <Select
                                labelId="new-key-provider-label"
                                label={t("provider") || "Provider"}
                                value={newKeyProvider}
                                onChange={(e) => {
                                    setNewKeyProvider(e.target.value);
                                    setNewKeyValidated(false);
                                    setNewKeyError('');
                                }}
                                disabled={newKeyValidating || editingProvider !== null}
                            >
                                <MenuItem value="openai" disabled={isProviderConfigured('openai')}>
                                    OpenAI {isProviderConfigured('openai') ? '(already configured)' : ''}
                                </MenuItem>
                                <MenuItem value="anthropic" disabled={isProviderConfigured('anthropic')}>
                                    Anthropic (Claude) {isProviderConfigured('anthropic') ? '(already configured)' : ''}
                                </MenuItem>
                                <MenuItem value="gemini" disabled={isProviderConfigured('gemini')}>
                                    Google Gemini {isProviderConfigured('gemini') ? '(already configured)' : ''}
                                </MenuItem>
                            </Select>
                        </FormControl>

                        {/* Step 2: Enter API Key */}
                        <TextField
                            fullWidth
                            label={t("api_key") || "API Key"}
                            type="password"
                            value={newKeyValue}
                            onChange={(e) => {
                                setNewKeyValue(e.target.value);
                                setNewKeyValidated(false);
                                setNewKeyError('');
                            }}
                            disabled={newKeyValidating}
                            sx={{mb: 2}}
                            placeholder="sk-..."
                            helperText={t("api_key_helper") || "Enter your API key from the provider"}
                        />

                        {/* Step 3: Validate Button */}
                        {!newKeyValidated && (
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleValidateApiKey}
                                disabled={!newKeyProvider || !newKeyValue || newKeyValidating}
                                startIcon={newKeyValidating ? <CircularProgress size={16}/> : <CheckCircleIcon/>}
                                sx={{mb: 2}}
                            >
                                {newKeyValidating ? (t("validating") || "Validating...") : (t("validate_api_key") || "Validate API Key")}
                            </Button>
                        )}

                        {/* Step 4: Success message (after validation) */}
                        {newKeyValidated && (
                            <>
                                <Alert severity="success" sx={{mb: 2}}>
                                    {t("api_key_valid") || "✅ API key is valid! You can now save it."}
                                </Alert>
                                <Typography variant="caption" display="block" sx={{mb: 2}} color="text.secondary">
                                    {t("api_key_model_hint") || "Note: You can select a provider and model from the dropdown above after saving the key."}
                                </Typography>
                            </>
                        )}

                        {/* Error message */}
                        {newKeyError && (
                            <Alert severity="error" sx={{mb: 2}}>
                                {newKeyError}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddKeyDialog}>
                        {t("cancel") || "Cancel"}
                    </Button>
                    <Button
                        onClick={handleSaveApiKey}
                        variant="contained"
                        disabled={!newKeyValidated}
                    >
                        {t("save") || "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* User Profile Dialog */}
            <UserProfileDialog
                open={profileOpen}
                onClose={() => setProfileOpen(false)}
                user={user}
                onUpdate={(updatedUser) => {
                    setUser(prev => ({ ...prev, ...updatedUser }));
                }}
            />

            <Box sx={{height: 8, flexShrink: 0}}/>
        </Box>
    );
}
